import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { cloneDeep } from "lodash-es"
import * as API from "services/api"
import { errorToast } from "shared/toast"

const SLICE_NAME = "answerOptionsLists"

export const PUBLIC_CONTEXT = "public-context"
const ACTIVITY_HUB_CONTEXT = "activity-hub-context"

const UNINITIALIZED = "uninitialized"
const INITIALIZING = "initializing"
const REFRESHING = "refreshing"
const LOADED = "loaded"

const INITIAL_STATE = {
  context: null,
  answerOptionsLists: [],
  status: UNINITIALIZED,
}

let setContext

const getApiFunction = (context, { activityHub: activityHubFunction, public: publicFunction }) => (
  context === ACTIVITY_HUB_CONTEXT ? activityHubFunction : publicFunction
)

export const initialize = createAsyncThunk(
  `${SLICE_NAME}/initialize`,
  // if context is PUBLIC_CONTEXT, formSlug is required.
  async ({ context = ACTIVITY_HUB_CONTEXT, formSlug } = {}, thunkAPI) => {
    const { dispatch, getState } = thunkAPI

    await dispatch(setContext({ context }))

    const apiFunction = getApiFunction(getState()[SLICE_NAME].context, {
      activityHub: API.getAnswerOptionsLists,
      public: () => API.getPublicAnswerOptionsLists({ formSlug }),
    })

    const response = await apiFunction()

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to load answer options.")
    }

    return { answerOptionsLists: response.data }
  },
)

export const answerOptionsSlice = createSlice({
  name: SLICE_NAME,
  initialState: cloneDeep(INITIAL_STATE),
  reducers: {
    setContext: (state, action) => {
      state.context = action.payload.context
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initialize.pending, (state) => {
        state.status = INITIALIZING
      })
      .addCase(initialize.fulfilled, (state, action) => {
        state.answerOptionsLists = action.payload.answerOptionsLists
        state.status = LOADED
      })
      .addCase(initialize.rejected, (state, action) => {
        state.status = LOADED
        errorToast(action.error.message)
      })
  },
})

setContext = answerOptionsSlice.actions.setContext

export const context = (state) => state[SLICE_NAME].context

export const answerOptionsLists = (state) => state[SLICE_NAME].answerOptionsLists

export const initializing = (state) => {
  const { status } = state[SLICE_NAME]
  return status === UNINITIALIZED || status === INITIALIZING
}

export const loading = (state) => {
  const { status } = state[SLICE_NAME]
  return status === INITIALIZING || status === REFRESHING
}

export default answerOptionsSlice.reducer
